import { Component } from "@theme/component";
import { ThemeEvents, QuantitySelectorUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { onAnimationEnd } from "@theme/utilities";
// SLEEPSAKE EDIT START — bundle pricing in the sticky bar. See CHANGES.md.
import { formatMoneyWithoutTrailingZeros } from "@theme/money-formatting";
// SLEEPSAKE EDIT END

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ["stickyBar", "addToCartButton", "quantityDisplay", "quantityNumber"];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    // SLEEPSAKE EDIT START — bundle pricing. Listen on the section so we don't
    // cross-talk with other product sections on the same page.
    target?.addEventListener(ThemeEvents.bundleChange, this.#handleBundleChange, { signal });
    this.#applyInitialBundleState();
    // SLEEPSAKE EDIT END

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest(".buy-buttons-block");
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector("footer") ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: "200px 0px 0px 0px",
      },
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = "true";
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    if (this.refs.addToCartButton.dataset.added !== "true") {
      this.refs.addToCartButton.dataset.added = "true";
    }

    // SLEEPSAKE EDIT (DEV-937): a bar with no image (a subscription's is its
    // button alone) has no fly-to-cart to wait for before the added state ends.
    if (!this.refs.productImage) {
      if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute("data-added");
      }, 800);
      return;
    }
    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement("fly-to-cart"));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add("fly-to-cart--sticky");
    flyToCartElement.style.setProperty("background-image", `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = "true";
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute("data-added");
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector("sticky-add-to-cart");
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute("data-stuck") || "false";
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute("data-stuck", currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();

    // SLEEPSAKE EDIT START — variant morph wipes our bundle price/badge/label
    // text. Re-derive from the currently-checked option so the sticky bar
    // keeps reflecting the bundle total instead of the variant default.
    // Deferred via microtask because both `<bundle-selector>` and this
    // component listen on the same section's `variant:update` and
    // listener order isn't guaranteed — the microtask ensures the
    // bundle-selector's dataset has settled with the new variant's unit /
    // compare prices before we read from it.
    queueMicrotask(() => this.#applyInitialBundleState());
    // SLEEPSAKE EDIT END
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(".sticky-add-to-cart__variant");
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll("input:checked"))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== "")
      .join(" / ");
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = "false";
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = "true";
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = "false";
  }

  // SLEEPSAKE EDIT START — bundle pricing in the sticky bar.
  /**
   * Handles `bundle:change` events dispatched by `<bundle-selector>` (see
   * `assets/bundle-selector.js`). Repaints the sticky bar's price block,
   * SAVE badge, and bundle short-label suffix to reflect the active option.
   *
   * @param {Event} event
   */
  #handleBundleChange = (event) => {
    const detail = /** @type {import("./bundle-selector").BundleChangeDetail | undefined} */ (
      /** @type {any} */ (event).detail
    );
    if (!detail) return;
    this.#repaintBundleState(detail);
  };

  /**
   * Derives the current bundle state from the section's `<bundle-selector>`
   * via its public `getCurrentDetail()` API and repaints. Used both:
   *
   * 1. On first connect, in case the `<bundle-selector>` had already mounted
   *    and dispatched its initial `bundle:change` before our listener was
   *    attached (custom-element upgrade order isn't guaranteed).
   * 2. After a variant-update morph wipes our injected text, to re-derive
   *    against the post-morph dataset (the bundle-selector updated its
   *    own `data-unit-price` / `data-compare-price` during the same event,
   *    but does not re-dispatch `bundle:change`).
   *
   * Bails silently if there's no bundle-selector in the section, or if the
   * element hasn't upgraded yet (the listener will pick up the eventual
   * dispatch).
   */
  #applyInitialBundleState() {
    const bundle =
      /** @type {HTMLElement & { getCurrentDetail?: () => import("./bundle-selector").BundleChangeDetail | null }} */ (
        this.closest(".shopify-section")?.querySelector("bundle-selector") ?? null
      );
    const detail = bundle?.getCurrentDetail?.() ?? null;
    if (detail) this.#repaintBundleState(detail);
  }

  /**
   * Repaint the sticky bar's price column + bundle label from a bundle-change
   * detail object. Toggles between Horizon's `.price__regular` and
   * `.price__sale` containers using the same `.price__hidden` modifier
   * that `<bundle-selector>` uses on the headline `<product-price>`, so the
   * two stay visually consistent.
   *
   * @param {import("./bundle-selector").BundleChangeDetail} detail
   */
  #repaintBundleState(detail) {
    const { bundlePrice, bundleCompare, totalDiscountPct, shortLabel } = detail;

    const priceContainer = /** @type {HTMLElement | null} */ (this.refs.priceContainer ?? null);

    // Volume-pricing branch in `snippets/price.liquid` renders a different
    // DOM tree (no `.price__regular` / `.price__sale`). Bundle math + volume
    // pricing are mutually exclusive, so bail out entirely — including the
    // SAVE badge — to stay consistent with `bundle-selector.js`'s headline
    // repaint, otherwise the badge would update against a price the bar
    // can't actually rewrite.
    if (priceContainer && !priceContainer.querySelector(".price__regular, .price__sale")) {
      return;
    }

    const bundleEl = this.closest(".shopify-section")?.querySelector("bundle-selector");
    const bundleDs = /** @type {HTMLElement | null} */ (bundleEl)?.dataset;
    const moneyFormat = bundleDs?.moneyFormat?.length ? bundleDs.moneyFormat : "${{amount}}";
    const moneyWithCurrencyFormat = bundleDs?.moneyWithCurrencyFormat?.length
      ? bundleDs.moneyWithCurrencyFormat
      : moneyFormat;
    const useCurrency = bundleDs?.currencyCodeEnabled === "true";
    const format = useCurrency ? moneyWithCurrencyFormat : moneyFormat;
    const currency = bundleDs?.currencyCode || "";

    const formattedPrice = formatMoneyWithoutTrailingZeros(bundlePrice, format, currency);
    const formattedCompare = formatMoneyWithoutTrailingZeros(bundleCompare, format, currency);
    const onSale = bundleCompare > bundlePrice;

    if (priceContainer) {
      const regular = priceContainer.querySelector(".price__regular");
      const sale = priceContainer.querySelector(".price__sale");
      if (onSale) {
        regular?.classList.add("price__hidden");
        sale?.classList.remove("price__hidden");
        const saleSpan = priceContainer.querySelector(".price__sale .price-item--sale");
        if (saleSpan) saleSpan.textContent = formattedPrice;
        const compareSpan = priceContainer.querySelector(".price__sale .price-item--regular.compare-at-price");
        if (compareSpan) compareSpan.textContent = formattedCompare;
      } else {
        sale?.classList.add("price__hidden");
        regular?.classList.remove("price__hidden");
        const regularSpan = priceContainer.querySelector(".price__regular .price");
        if (regularSpan) regularSpan.textContent = formattedPrice;
      }
    }

    const badge = /** @type {HTMLElement | undefined} */ (this.refs.discountBadge);
    if (badge) {
      const pct = Math.round(Number(totalDiscountPct) || 0);
      if (onSale && pct > 0) {
        badge.textContent = `SAVE ${pct}%`;
        badge.hidden = false;
      } else {
        badge.textContent = "";
        badge.hidden = true;
      }
    }

    const label = /** @type {HTMLElement | undefined} */ (this.refs.bundleLabel);
    if (label) {
      // The merchant's `short_label` setting supplies its own surrounding
      // characters (e.g. "(2 Pack)"); we just write it in. Visual spacing
      // from the title comes from `margin-inline-start` in CSS.
      const trimmed = (shortLabel || "").trim();
      if (trimmed) {
        label.textContent = trimmed;
        label.hidden = false;
      } else {
        label.textContent = "";
        label.hidden = true;
      }
    }
  }
  // SLEEPSAKE EDIT END

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace("shopify-section-", "");
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`,
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || "1") || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = "inline";
    } else {
      quantityDisplay.style.display = "none";
    }
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCartComponent);
}
